import React, { useState } from 'react';
import ReactSelect from 'react-select';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Textarea,
    VStack,
    HStack,
    Radio,
    RadioGroup,
    Select,
    useToast,
    Text,
    Box,
    Alert,
    AlertIcon,
    Badge,
    SimpleGrid
} from '@chakra-ui/react';
import { FiSend, FiUsers, FiUser } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';

const TARGET_TYPES = [
    {
        value: 'users',
        label: 'طلاب محددين',
        description: 'الإرسال لأرقام الطلاب المختارين',
        icon: '👨‍🎓',
        color: 'blue'
    },
    {
        value: 'parent_specific',
        label: 'أولياء أمور محددين',
        description: 'الإرسال لأرقام أولياء أمور الطلاب المختارين',
        icon: '👨‍👩‍👦',
        color: 'green'
    },
    {
        value: 'all_parents',
        label: 'جميع أولياء الأمور',
        description: 'إرسال جماعي لأولياء أمور كل الطلاب',
        icon: '👪',
        color: 'orange'
    },
    {
        value: 'group',
        label: 'مجموعة (WhatsApp)',
        description: 'الإرسال لمجموعة حضور واتساب',
        icon: '💬',
        color: 'purple'
    }
];

const SendSMSModal = ({ isOpen, onClose, users = [], attendanceGroups = [] }) => {
    const [targetType, setTargetType] = useState('users');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedMatier, setSelectedMatier] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    // Show students (role !== 'admin' && role !== 'instructor')
    const studentUsers = (users || []).filter(u => u.role !== 'admin' && u.role !== 'instructor');

    const studentOptions = studentUsers.map(user => ({
        value: user._id,
        label: `${user.username || user.name || 'طالب'} — ولي الأمر: ${user.parentPhoneNumber || user.whatsappNumberParent || '—'} / الطالب: ${user.phoneNumber || '—'}`
    }));

    const userOptions = (users || []).map(user => ({
        value: user._id,
        label: `${user.username || user.name || 'مستخدم'} (${user.phoneNumber || user.parentPhoneNumber || user.whatsappNumberUser || 'بلا هاتف'})`
    }));

    const availableGroups = (attendanceGroups || []).find(g => g.matier === selectedMatier)?.groups || [];


    const handleTargetChange = (val) => {
        setTargetType(val);
        setSelectedUsers([]);
        setSelectedMatier('');
        setSelectedGroup('');
    };

    const handleSend = async () => {
        if (!message.trim()) {
            toast({ title: 'يرجى كتابة نص الرسالة', status: 'warning' });
            return;
        }

        if ((targetType === 'users' || targetType === 'parent_specific') && selectedUsers.length === 0) {
            toast({ title: 'يرجى اختيار مستخدم واحد على الأقل', status: 'warning' });
            return;
        }

        if (targetType === 'group' && (!selectedMatier || !selectedGroup)) {
            toast({ title: 'يرجى اختيار المادة والمجموعة', status: 'warning' });
            return;
        }

        if (targetType === 'all_parents') {
            const confirmed = window.confirm(
                `سيتم إرسال الرسالة لأولياء أمور جميع الطلاب (${studentUsers.length} طالب). هل أنت متأكد؟`
            );
            if (!confirmed) return;
        }

        setIsLoading(true);
        try {
            const payload = {
                targetType,
                message: message.trim(),
                userIds: (targetType === 'users' || targetType === 'parent_specific')
                    ? selectedUsers.map(u => u.value)
                    : [],
                matier: selectedMatier,
                groupName: selectedGroup
            };

            const response = await attendanceService.sendManualSMS(payload);
            if (response.success) {
                toast({
                    title: 'تمت العملية بنجاح',
                    description: response.message,
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                });
                onClose();
                setMessage('');
                setSelectedUsers([]);
                setSelectedMatier('');
                setSelectedGroup('');
            } else {
                toast({
                    title: 'خطأ',
                    description: response.message,
                    status: 'error'
                });
            }
        } catch (error) {
            toast({
                title: 'خطأ في الاتصال',
                description: error.response?.data?.message || 'فشل في إرسال الطلب',
                status: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const currentTarget = TARGET_TYPES.find(t => t.value === targetType);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" dir="rtl">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" fontFamily="Tajawal, sans-serif">
                <ModalHeader borderBottomWidth="1px">
                    <HStack spacing={3}>
                        <Text fontSize="xl">📱</Text>
                        <Text>إرسال رسالة SMS مخصصة</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton left="8px" right="auto" />

                <ModalBody p={6}>
                    <VStack spacing={6} align="stretch">

                        {/* Target Type Selection */}
                        <FormControl>
                            <FormLabel fontWeight="bold">إرسال إلى:</FormLabel>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                                {TARGET_TYPES.map(t => (
                                    <Box
                                        key={t.value}
                                        p={3}
                                        borderRadius="lg"
                                        border="2px solid"
                                        borderColor={targetType === t.value ? `${t.color}.400` : 'gray.200'}
                                        bg={targetType === t.value ? `${t.color}.50` : 'white'}
                                        cursor="pointer"
                                        onClick={() => handleTargetChange(t.value)}
                                        transition="all 0.15s"
                                        textAlign="center"
                                    >
                                        <Text fontSize="xl" mb={1}>{t.icon}</Text>
                                        <Text
                                            fontSize="xs"
                                            fontWeight="700"
                                            color={targetType === t.value ? `${t.color}.700` : 'gray.600'}
                                        >
                                            {t.label}
                                        </Text>
                                    </Box>
                                ))}
                            </SimpleGrid>
                            {currentTarget && (
                                <Text fontSize="xs" color="gray.500" mt={2}>
                                    ℹ️ {currentTarget.description}
                                </Text>
                            )}
                        </FormControl>

                        {/* Conditional inputs */}
                        {(targetType === 'users') && (
                            <FormControl>
                                <FormLabel fontWeight="bold">اختر الطلاب / المستخدمين</FormLabel>
                                <Text fontSize="xs" color="blue.500" mb={2}>
                                    ستُرسل الرسالة لرقم هاتف الطالب مباشرة
                                </Text>
                                <ReactSelect
                                    isMulti
                                    options={userOptions}
                                    value={selectedUsers}
                                    onChange={setSelectedUsers}
                                    placeholder="ابحث عن المستخدمين..."
                                    styles={{
                                        control: (base) => ({ ...base, textAlign: 'right' }),
                                        menu: (base) => ({ ...base, textAlign: 'right' }),
                                    }}
                                />
                            </FormControl>
                        )}

                        {targetType === 'parent_specific' && (
                            <FormControl>
                                <FormLabel fontWeight="bold">اختر الطلاب (يُرسَل لأولياء أمورهم)</FormLabel>
                                <Text fontSize="xs" color="green.600" mb={2}>
                                    ستُرسل الرسالة لرقم ولي الأمر الخاص بكل طالب مختار
                                </Text>
                                <ReactSelect
                                    isMulti
                                    options={studentOptions}
                                    value={selectedUsers}
                                    onChange={setSelectedUsers}
                                    placeholder="ابحث عن الطلاب..."
                                    styles={{
                                        control: (base) => ({ ...base, textAlign: 'right' }),
                                        menu: (base) => ({ ...base, textAlign: 'right' }),
                                    }}
                                />
                            </FormControl>
                        )}

                        {targetType === 'all_parents' && (
                            <Alert status="warning" borderRadius="lg">
                                <AlertIcon />
                                <VStack align="start" spacing={0}>
                                    <Text fontWeight="700" fontSize="sm">إرسال جماعي لأولياء الأمور</Text>
                                    <Text fontSize="xs">
                                        ستُرسل الرسالة لأرقام أولياء أمور جميع الطلاب المسجلين ({studentUsers.length} طالب).
                                        تأكد من صحة نص الرسالة قبل الإرسال.
                                    </Text>
                                </VStack>
                            </Alert>
                        )}

                        {targetType === 'group' && (
                            <HStack align="end" spacing={4}>
                                <FormControl>
                                    <FormLabel fontWeight="bold">المادة</FormLabel>
                                    <Select
                                        placeholder="اختر المادة"
                                        value={selectedMatier}
                                        onChange={(e) => { setSelectedMatier(e.target.value); setSelectedGroup(''); }}
                                    >
                                        {attendanceGroups.map(g => (
                                            <option key={g._id} value={g.matier}>{g.matier}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontWeight="bold">المجموعة</FormLabel>
                                    <Select
                                        placeholder="اختر المجموعة"
                                        value={selectedGroup}
                                        onChange={(e) => setSelectedGroup(e.target.value)}
                                        isDisabled={!selectedMatier}
                                    >
                                        {availableGroups.map((g, i) => (
                                            <option key={i} value={g}>{g}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </HStack>
                        )}

                        {/* Message */}
                        <FormControl>
                            <FormLabel fontWeight="bold">نص الرسالة</FormLabel>
                            <Textarea
                                placeholder="اكتب رسالتك هنا..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={5}
                                borderRadius="lg"
                            />
                            <HStack justify="space-between" mt={1}>
                                <Text fontSize="xs" color="gray.500">
                                    سيتم الإرسال عبر تطبيق الأندرويد المرتبط بالنظام
                                </Text>
                                <Badge colorScheme={message.length > 160 ? 'orange' : 'gray'} fontSize="xs">
                                    {message.length} حرف
                                </Badge>
                            </HStack>
                        </FormControl>

                    </VStack>
                </ModalBody>

                <ModalFooter bg="gray.50" borderBottomRadius="xl">
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<FiSend />}
                            colorScheme={currentTarget ? currentTarget.color : 'blue'}
                            onClick={handleSend}
                            isLoading={isLoading}
                            loadingText="جاري الإرسال..."
                        >
                            {targetType === 'all_parents' ? 'إرسال لجميع الأولياء' :
                                targetType === 'parent_specific' ? 'إرسال لأولياء الأمور' :
                                    'إرسال الرسائل'}
                        </Button>
                        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SendSMSModal;

